import React, { useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import StatusBadge from './StatusBadge';
import { toast } from 'react-toastify';

const ManagePanel = ({ subscription, plans, onUpgrade }) => {
  const { handleCancelSubscription, handleSetBillingAutoRenew } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  if (!subscription) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-4">📋</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Active Subscription</h3>
          <p className="text-gray-600 mb-4">You don't have an active subscription yet.</p>
          <button
            onClick={() => onUpgrade && onUpgrade()}
            className="bg-[#BD2026] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#9a1a1f] transition-colors"
          >
            Browse Plans
          </button>
        </div>
      </div>
    );
  }

  const currentPlan = plans?.find(p => p.planCode === subscription.planCode);
  const hasStripeSubscription = subscription.stripeSubscriptionId;
  const canCancel = subscription.status === 'ACTIVE' || subscription.status === 'TRIAL';
  const canToggleAutoRenew = hasStripeSubscription && subscription.status === 'ACTIVE';

  const handleCancel = async () => {
    if (!hasStripeSubscription) {
      // For non-Stripe subscriptions, use the regular cancel
      setIsProcessing(true);
      try {
        await handleCancelSubscription(subscription.userId);
        setShowCancelConfirm(false);
      } catch (error) {
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // For Stripe subscriptions, use the billing endpoint
    setIsProcessing(true);
    try {
      await handleSetBillingAutoRenew(subscription.stripeSubscriptionId, false);
      setShowCancelConfirm(false);
    } catch (error) {
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    if (!hasStripeSubscription) {
      toast.error('No Stripe subscription found. Cannot toggle auto-renew.');
      return;
    }
    
    setIsProcessing(true);
    try {
      const newAutoRenewState = !subscription.autoRenew;
      const action = newAutoRenewState ? 'enable' : 'disable';
      
      const result = await handleSetBillingAutoRenew(subscription.stripeSubscriptionId, newAutoRenewState);
      
      if (result.payload) {
        toast.success(`Auto-renew ${action}d successfully!`);
        // Refresh the subscription data to show updated state
        window.location.reload();
      } else {
        toast.error(`Failed to ${action} auto-renew. Please try again.`);
      }
    } catch (error) {
      console.error('Auto-renew toggle error:', error);
      toast.error(`Failed to toggle auto-renew: ${error.message || 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-xl p-6 border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Subscription Management</h2>
        <StatusBadge status={subscription.status} size="lg" />
      </div>

      {/* Current Plan Info */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-800 mb-3">Current Plan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-600">Plan:</span>
            <p className="text-gray-800 font-medium">{currentPlan?.name || subscription.planCode}</p>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-600">Status:</span>
            <p className="text-gray-800">{subscription.status}</p>
          </div>
          {subscription.currentPeriodStart && (
            <div>
              <span className="text-sm font-medium text-gray-600">Current Period Start:</span>
            <p className="text-gray-800">{formatDate(subscription.currentPeriodStart)}</p>
          </div>
          )}
          {subscription.currentPeriodEnd && (
            <div>
              <span className="text-sm font-medium text-gray-600">Next Billing Date:</span>
              <p className="text-gray-800">{formatDate(subscription.currentPeriodEnd)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="space-y-4">
        {/* Upgrade Plan */}
        {onUpgrade && (
          <div className="mb-4">
            <button
              onClick={onUpgrade}
              className="w-full bg-[#BD2026] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#9a1a1f] transition-colors"
            >
              Update Plan
            </button>
          </div>
        )}

        {/* Auto-renew Toggle */}
        {canToggleAutoRenew && (
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <h4 className="font-medium text-blue-800">Auto-renewal</h4>
              <p className="text-sm text-blue-600">
                {subscription.autoRenew ? 'Your subscription will automatically renew' : 'Your subscription will not automatically renew'}
              </p>
            </div>
            <button
              onClick={handleToggleAutoRenew}
              disabled={isProcessing}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                subscription.autoRenew
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              {isProcessing ? 'Processing...' : 
                subscription.autoRenew ? 'Disable' : 'Enable'
              }
            </button>
          </div>
        )}

        {/* Cancel Subscription */}
        {canCancel && (
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-800">Cancel Subscription</h4>
                <p className="text-sm text-gray-600">
                  {hasStripeSubscription 
                    ? 'Cancel at the end of your current billing period'
                    : 'Cancel your subscription immediately'
                  }
                </p>
              </div>
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={isProcessing}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"></div>
            
            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="text-center">
                <div className="text-red-600 text-4xl mb-4">⚠️</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  What would you like to do?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Choose how you'd like to modify your subscription:
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={() => {/* TODO: Implement downgrade logic */}}
                    disabled={isProcessing}
                    className="w-full bg-[#BD2026] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#9a1a1f] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    Downgrade my account
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isProcessing}
                    className="w-full bg-gray-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {isProcessing ? 'Processing...' : 'Cancel subscription'}
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={isProcessing}
                    className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    Keep current plan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePanel;
